/**
 * Shared paginated fetch for the pipeline switcher board set (SE-5489).
 *
 * Both switcher surfaces — the chip bar (`PipelineChipsRowContainer`) and the
 * all-pipelines drawer (`useSeeAllPipelinesData`) — read the current user's
 * kanban boards from `listMyKanbanBoards` and then client-filter to
 * PIPELINE-source boards. `listMyKanbanBoards` is a Relay connection, so a
 * single `first: 100` page silently drops every board past the first page: an
 * org with >100 total boards would lose pipelines from BOTH surfaces.
 *
 * This hook owns the fix in one place: it walks `pageInfo.hasNextPage` /
 * `endCursor` with `fetchMore` until the connection is exhausted (or the page
 * cap is hit), merging each page onto the accumulated result via `updateQuery`.
 * It mirrors the in-repo `RulesPanel` pagination pattern (fetchMore +
 * updateQuery merge + a per-cursor request ledger) rather than adding a global
 * `relayStylePagination` type policy — `listMyKanbanBoards` is deliberately
 * kept out of the Apollo cache field policies because a second query
 * (`ListMyKanbanBoards`) selects a different field set on the same connection,
 * and a shared field policy would cross-contaminate the two.
 *
 * `$first` is fixed at {@link PIPELINE_SWITCHER_PAGE_SIZE} at every call site so
 * the query collapses to one normalized cache entry (no cache-key
 * fragmentation — SE-5058).
 * @module features/player-kanban/hooks/usePipelineSwitcherBoards
 */

import type { WatchQueryFetchPolicy } from "@apollo/client";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  PipelineSwitcherBoardsQuery,
  usePipelineSwitcherBoardsQuery,
} from "@/generated/graphql";

/** Page size for the switcher board list. Shared across call sites so the query collapses to one cache entry. */
export const PIPELINE_SWITCHER_PAGE_SIZE = 100;

/**
 * Hard cap on the number of ADDITIONAL pages walked after the first, so a
 * pathological board count cannot spin an unbounded fetch loop. At the page
 * size above this covers 100 * (1 + {@link MAX_SWITCHER_EXTRA_PAGES}) boards.
 *
 * SE-6597: hitting it is reported through `hasUnreachableBoards` so the surface
 * can say boards are missing. It used to map to a bare `console.warn`, which no
 * user ever sees — the chip row simply looked complete and was not.
 */
export const MAX_SWITCHER_EXTRA_PAGES = 19;

/** A single edge from the switcher board query. */
export type PipelineSwitcherEdge =
  PipelineSwitcherBoardsQuery["listMyKanbanBoards"]["edges"][number];

/**
 * Merge one fetched switcher page onto the accumulated result by concatenating
 * edges and adopting the newest page's `pageInfo` (so the walk terminates).
 * @param previous - The result accumulated so far.
 * @param next - The freshly fetched page (undefined on an empty refetch).
 * @returns The combined query result.
 */
export const mergeSwitcherPages = (
  previous: PipelineSwitcherBoardsQuery,
  next: PipelineSwitcherBoardsQuery | undefined
): PipelineSwitcherBoardsQuery => {
  if (!next) return previous;
  return {
    ...next,
    listMyKanbanBoards: {
      ...next.listMyKanbanBoards,
      edges: [
        ...previous.listMyKanbanBoards.edges,
        ...next.listMyKanbanBoards.edges,
      ],
    },
  };
};

/**
 * Collapse edges to one per board `id`, keeping the first occurrence and
 * preserving order. Guards the read boundary against an updateQuery merge ever
 * holding two edges for the same board.
 * @param edges - The accumulated edges.
 * @returns Edges with at most one entry per board id.
 */
const dedupeEdgesById = (
  edges: readonly PipelineSwitcherEdge[]
): readonly PipelineSwitcherEdge[] =>
  edges.filter(
    (edge, index) =>
      edges.findIndex(other => other.node.id === edge.node.id) === index
  );

/** Arguments for {@link usePipelineSwitcherBoards}. */
export interface UsePipelineSwitcherBoardsArgs {
  /** When true, the query is skipped entirely (e.g. a closed drawer). */
  readonly skip?: boolean;
  /** Apollo fetch policy for the underlying query. */
  readonly fetchPolicy?: WatchQueryFetchPolicy;
}

/** Return shape of {@link usePipelineSwitcherBoards}. */
export interface UsePipelineSwitcherBoardsResult {
  /** Every board edge across all fetched pages, de-duplicated by id. */
  readonly edges: readonly PipelineSwitcherEdge[];
  /** Apollo loading state for the first page. */
  readonly loading: boolean;
  /** Whether the switcher board query failed. */
  readonly error: boolean;
  /** Refetch the board set from the first page (recovery from an error). */
  readonly retry: () => void;
  /**
   * SE-6597: true when the walk stopped while the connection still reported
   * more pages — the page cap was spent, a page request failed, or a page
   * claimed more pages but gave no cursor to follow. The board set is
   * incomplete and the surface must say so rather than truncate silently.
   *
   * Named and scoped to match `hasUnreachableBoards` on SE-6575's
   * `useAllMyKanbanBoards`, so the two switcher surfaces report the same thing
   * instead of growing two vocabularies for one condition.
   */
  readonly hasUnreachableBoards: boolean;
}

/**
 * Fetch the whole switcher board set, walking every page of the
 * `listMyKanbanBoards` connection so no pipeline is dropped past the first page.
 * @param args - {@link UsePipelineSwitcherBoardsArgs}
 * @param args.skip - Skip the query (closed surface) — default false.
 * @param args.fetchPolicy - Apollo fetch policy — default undefined (cache-first).
 * @returns The {@link UsePipelineSwitcherBoardsResult}: merged edges, loading, error.
 */
export const usePipelineSwitcherBoards = ({
  skip = false,
  fetchPolicy,
}: UsePipelineSwitcherBoardsArgs = {}): UsePipelineSwitcherBoardsResult => {
  const { data, loading, error, fetchMore, refetch } =
    usePipelineSwitcherBoardsQuery({
      variables: { first: PIPELINE_SWITCHER_PAGE_SIZE },
      skip,
      fetchPolicy,
    });

  const pageInfo = data?.listMyKanbanBoards?.pageInfo;
  const hasNextPage = pageInfo?.hasNextPage ?? false;
  const endCursor = pageInfo?.endCursor ?? null;

  const fetchNext = useCallback(
    (after: string) =>
      fetchMore({
        variables: { after },
        updateQuery: (previous, { fetchMoreResult }) =>
          mergeSwitcherPages(previous, fetchMoreResult),
      }),
    [fetchMore]
  );

  const requestedCursors = useRef<Set<string>>(new Set());
  // A mid-walk `fetchMore` rejection (network hiccup after the first page) would
  // otherwise silently truncate the list — the cursor is already in the ledger
  // so it is never retried. Track it so the surface reflects a real failure
  // rather than a quietly-short board set.
  const [paginationError, setPaginationError] = useState(false);

  // SE-6597: "the walk has spent its whole page budget", set from the promise
  // continuation of the last page the cap allows — NOT from the effect body,
  // where a setState is barred (react-hooks/set-state-in-effect) and where a
  // ref read would be impure. On its own it does not mean the list is short:
  // the final allowed page may well be the last page, which is why the reported
  // flag below ANDs it with `hasNextPage`.
  const [spentPageBudget, setSpentPageBudget] = useState(false);

  // "Adjust state during render": when the query flips to skipped, clear a stale
  // pagination error so the next open doesn't show a failure from a prior
  // session — without a setState-in-effect (react-hooks/set-state-in-effect).
  // The failed-cursor ledger (a ref) is reset in the walk effect below, since
  // refs must not be mutated during render.
  const [prevSkip, setPrevSkip] = useState(skip);
  if (prevSkip !== skip) {
    setPrevSkip(skip);
    if (skip) {
      setPaginationError(false);
      setSpentPageBudget(false);
    }
  }

  const edges = useMemo(
    (): readonly PipelineSwitcherEdge[] =>
      dedupeEdgesById(data?.listMyKanbanBoards?.edges ?? []),
    [data]
  );

  // Recovery: clear the failed-cursor ledger + pagination error and refetch from
  // the first page so the walk restarts cleanly.
  const retry = useCallback(() => {
    requestedCursors.current = new Set();
    setPaginationError(false);
    setSpentPageBudget(false);
    void refetch();
  }, [refetch]);

  // SE-6597: the walk stopped short. DERIVED during render from the three ways
  // that happens, every one of them gated on the connection still reporting
  // more pages — so a walk that ended because it genuinely reached the last
  // page never lights the affordance:
  //
  //  - `spentPageBudget` — the page cap was reached (above).
  //  - `paginationError` — a page request failed. Its cursor is already in the
  //    ledger and is never retried, so the list stops there.
  //  - `endCursor === null` — the connection claims more pages but hands back
  //    nothing to follow. The walk effect returns early on exactly this, which
  //    was previously the quietest truncation of the three: no log at all.
  //
  // Deliberately NOT derived from the edge count. Under SE-6577's newest-first
  // ordering a truncated window's contents depend on what was created most
  // recently, so any "we must have N boards by now" arithmetic would encode an
  // ordering assumption. Each term above is the literal stop condition instead.
  const hasUnreachableBoards =
    hasNextPage && (spentPageBudget || paginationError || endCursor === null);

  // Walk every remaining page so the switcher holds the WHOLE board set. Each
  // distinct cursor is requested at most once (guarded by a ref) so rapid
  // re-renders cannot double-append a page; the ledger resets when the query is
  // skipped. The page cap prevents an unbounded loop and is logged when hit.
  useEffect(() => {
    if (skip) {
      requestedCursors.current = new Set();
      return;
    }
    if (!hasNextPage || !endCursor) return;
    if (requestedCursors.current.has(endCursor)) return;
    if (requestedCursors.current.size >= MAX_SWITCHER_EXTRA_PAGES) {
      console.warn(
        "[usePipelineSwitcherBoards] page cap reached; some pipelines beyond " +
          `${PIPELINE_SWITCHER_PAGE_SIZE * (MAX_SWITCHER_EXTRA_PAGES + 1)} ` +
          "boards are not shown"
      );
      return;
    }
    // SE-6597: is this the last page the cap allows? Decided BEFORE the request
    // so the answer is not read back off a ref later, and reported from the
    // continuation below so no setState happens in this effect body.
    const isFinalAllowedPage =
      requestedCursors.current.size === MAX_SWITCHER_EXTRA_PAGES - 1;
    requestedCursors.current.add(endCursor);
    // Catch a rejected page walk: surface it as an error (so the consumer can
    // show a failure state) instead of leaking an unhandled promise rejection
    // and silently stopping with `hasNextPage` still true.
    fetchNext(endCursor)
      .then(() => {
        if (isFinalAllowedPage) setSpentPageBudget(true);
      })
      .catch((err: unknown) => {
        console.warn("[usePipelineSwitcherBoards] fetchMore failed", err);
        setPaginationError(true);
      });
  }, [skip, hasNextPage, endCursor, fetchNext]);

  return {
    edges,
    loading,
    error: Boolean(error) || paginationError,
    retry,
    hasUnreachableBoards,
  };
};
